import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import Header from "@/components/Header";
import { useAuth } from "@/lib/auth";
import { apiClient } from "@/lib/api";
import { capture } from "@/lib/analytics";
import { ScrollableTabs } from "@/components/ScrollableTabs";
import EffectivenessPanel from "@/components/EffectivenessPanel";
import {
  Inbox,
  Tag,
  Plus,
  Power,
  Trash2,
  ExternalLink,
  Search,
  Copy,
  Check,
  Pencil,
  BarChart3,
  TrendingUp,
  Users,
  PlayCircle,
  Award,
  CreditCard,
  X,
} from "lucide-react";

const STATUS_COLORS = {
  new: { bg: "rgba(255,92,42,0.08)", border: "rgba(255,92,42,0.3)", color: "var(--accent)" },
  contacted: { bg: "rgba(79,70,229,0.08)", border: "rgba(79,70,229,0.3)", color: "var(--indigo)" },
  won: { bg: "rgba(14,159,110,0.08)", border: "rgba(14,159,110,0.3)", color: "var(--emerald)" },
  lost: { bg: "rgba(15,15,18,0.06)", border: "rgba(15,15,18,0.16)", color: "var(--ink-2)" },
};

export default function Admin() {
  const { user, loading: authLoading } = useAuth();
  const [tab, setTab] = useState("analytics");

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <span className="spinner" />
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  if (!user.is_admin) {
    return (
      <div className="App min-h-screen">
        <Header />
        <main className="max-w-2xl mx-auto px-4 md:px-8 py-20 text-center">
          <h1 className="font-display text-4xl tracking-tight">Not authorized</h1>
          <p className="text-[var(--ink-2)] mt-3">
            This page is only available to admins. If you should have access, add your email to <code className="px-1.5 py-0.5 rounded bg-[var(--bg-2)] font-mono text-sm">ADMIN_EMAILS</code> in <code className="px-1.5 py-0.5 rounded bg-[var(--bg-2)] font-mono text-sm">backend/.env</code>.
          </p>
        </main>
      </div>
    );
  }

  return (
    <div className="App min-h-screen">
      <Header />
      <main className="max-w-7xl mx-auto px-4 md:px-8 py-12">
        <span className="kicker">Internal</span>
        <h1 className="font-display text-5xl mt-3 leading-tight tracking-tight">
          Admin
        </h1>

        <div className="mt-7 max-w-xl">
          <ScrollableTabs
            value={tab}
            onChange={setTab}
            tabs={[
              { id: "analytics", label: "Analytics" },
              { id: "inquiries", label: "Inquiries" },
              { id: "promos",    label: "Promo codes" },
              { id: "effectiveness", label: "Effectiveness Lab" },
            ]}
            testidPrefix="admin-tab"
          />
        </div>

        <div className="mt-7">
          {tab === "analytics" ? <AnalyticsPanel />
            : tab === "inquiries" ? <InquiriesPanel />
            : tab === "effectiveness" ? <EffectivenessPanel />
            : <PromosPanel />}
        </div>
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inquiries
// ---------------------------------------------------------------------------
function InquiriesPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [updating, setUpdating] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiClient.get("/admin/inquiries");
      setItems(r.data.items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const s = q.toLowerCase().trim();
    return items.filter((i) => {
      if (statusFilter !== "all" && (i.status || "new") !== statusFilter) return false;
      if (!s) return true;
      return (
        i.company_name?.toLowerCase().includes(s) ||
        i.contact_name?.toLowerCase().includes(s) ||
        i.work_email?.toLowerCase().includes(s) ||
        i.industry?.toLowerCase().includes(s) ||
        i.message?.toLowerCase().includes(s)
      );
    });
  }, [items, q, statusFilter]);

  const setStatus = async (id, status) => {
    setUpdating(id);
    try {
      await apiClient.patch(`/admin/inquiries/${id}`, { status });
      setItems((arr) =>
        arr.map((x) => (x.id === id ? { ...x, status } : x))
      );
    } finally {
      setUpdating(null);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap gap-3 items-center mb-5">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--ink-3)]" />
          <input
            className="brut-input pl-10"
            placeholder="Search company, name, email…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="admin-inquiries-search"
          />
        </div>
        <div className="inline-flex rounded-full border border-[var(--line-strong)] p-0.5 bg-[var(--surface)]">
          {["all", "new", "contacted", "won", "lost"].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors capitalize ${statusFilter === s ? "bg-[var(--ink)] text-white" : "text-[var(--ink-2)]"}`}
              data-testid={`admin-inquiries-filter-${s}`}
            >
              {s}
            </button>
          ))}
        </div>
        <span className="text-sm text-[var(--ink-3)] ml-auto">
          {filtered.length} of {items.length}
        </span>
      </div>

      {loading ? (
        <div className="brut p-10 text-center"><span className="spinner" /></div>
      ) : filtered.length === 0 ? (
        <div className="brut p-12 text-center text-[var(--ink-2)]">
          No inquiries yet. They'll show up here when teams submit the form.
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((it) => {
            const status = it.status || "new";
            const palette = STATUS_COLORS[status] || STATUS_COLORS.new;
            return (
              <div
                key={it.id}
                className="brut p-5"
                data-testid={`admin-inquiry-row-${it.id}`}
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-display text-2xl tracking-tight leading-tight">
                        {it.company_name}
                      </h3>
                      <span
                        className="pill capitalize"
                        style={{
                          background: palette.bg,
                          borderColor: palette.border,
                          color: palette.color,
                        }}
                      >
                        {status}
                      </span>
                      <span className="pill">{it.team_size}</span>
                    </div>
                    <div className="text-sm text-[var(--ink-2)] mt-1">
                      <span className="text-[var(--ink)]">{it.contact_name}</span> ·{" "}
                      <a href={`mailto:${it.work_email}`} className="hover:text-[var(--ink)] underline underline-offset-2 decoration-[var(--line-strong)]">
                        {it.work_email}
                      </a>
                      {it.phone && <> · {it.phone}</>}
                      {it.industry && <> · {it.industry}</>}
                    </div>
                    {it.message && (
                      <div className="mt-3 text-sm text-[var(--ink)] leading-relaxed bg-[var(--bg)] border border-[var(--line)] rounded-lg p-3 whitespace-pre-wrap">
                        {it.message}
                      </div>
                    )}
                    <div className="text-xs text-[var(--ink-3)] mt-2">
                      Received {new Date(it.created_at).toLocaleString()}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    {["new", "contacted", "won", "lost"].map((s) => (
                      <button
                        key={s}
                        onClick={() => setStatus(it.id, s)}
                        disabled={updating === it.id || status === s}
                        className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-all ${
                          status === s
                            ? "border-[var(--ink)] bg-[var(--ink)] text-white"
                            : "border-[var(--line)] text-[var(--ink-2)] hover:border-[var(--ink)] hover:text-[var(--ink)]"
                        }`}
                        data-testid={`admin-inquiry-set-${s}-${it.id}`}
                      >
                        {s === "new" ? "Mark new" : s === "contacted" ? "Contacted" : s === "won" ? "Won 🎉" : "Lost"}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Promos
// ---------------------------------------------------------------------------
const NEW_PROMO = {
  code: "",
  discount_type: "percent",
  discount_value: 10,
  applies_to: "both",
  max_uses: "",
  max_uses_per_user: 1,
  new_customers_only: false,
  expires_at: "",
  active: true,
  note: "",
};

function discountTypeLabel(t) {
  return { percent: "Percent off", amount: "Dollars off", fixed_price: "Fixed first-month price" }[t] || t;
}

function PromosPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [draft, setDraft] = useState(NEW_PROMO);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(null);
  const [editing, setEditing] = useState(null); // code being edited
  const [editDraft, setEditDraft] = useState(null);
  const [editError, setEditError] = useState(null);
  const [usageOpen, setUsageOpen] = useState(null); // code whose usage panel is open
  const [usageData, setUsageData] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiClient.get("/admin/promos");
      setItems(r.data.items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const buildPayload = (d) => ({
    discount_type: d.discount_type,
    discount_value: Number(d.discount_value),
    applies_to: d.applies_to,
    max_uses: d.max_uses === "" || d.max_uses == null ? null : Number(d.max_uses),
    max_uses_per_user:
      d.max_uses_per_user === "" || d.max_uses_per_user == null
        ? null
        : Number(d.max_uses_per_user),
    new_customers_only: !!d.new_customers_only,
    expires_at: d.expires_at || null,
    active: d.active,
    note: d.note || null,
  });

  const create = async () => {
    setError(null);
    setBusy(true);
    try {
      const payload = { code: draft.code, ...buildPayload(draft) };
      await apiClient.post("/admin/promos", payload);
      capture("promo_created", {
        code_prefix: (draft.code || "").slice(0, 6),
        discount_type: draft.discount_type,
        discount_value: Number(draft.discount_value),
        applies_to: draft.applies_to,
        max_uses: payload.max_uses,
        max_uses_per_user: payload.max_uses_per_user,
        new_customers_only: payload.new_customers_only,
        has_expiry: !!payload.expires_at,
      });
      setDraft(NEW_PROMO);
      setShowNew(false);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Could not create promo code.");
    } finally {
      setBusy(false);
    }
  };

  const openEdit = (p) => {
    setEditing(p.code);
    setEditDraft({
      ...p,
      max_uses: p.max_uses ?? "",
      max_uses_per_user: p.max_uses_per_user ?? 1,
      new_customers_only: !!p.new_customers_only,
      expires_at: p.expires_at || "",
      note: p.note || "",
    });
    setEditError(null);
  };

  const saveEdit = async () => {
    setEditError(null);
    setBusy(true);
    try {
      await apiClient.put(`/admin/promos/${editing}`, buildPayload(editDraft));
      setEditing(null);
      setEditDraft(null);
      await load();
    } catch (e) {
      setEditError(e?.response?.data?.detail || e.message || "Could not save changes.");
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (code) => {
    await apiClient.patch(`/admin/promos/${code}`);
    await load();
  };

  const remove = async (code) => {
    if (!window.confirm(`Delete promo code "${code}"? This can't be undone.`)) return;
    await apiClient.delete(`/admin/promos/${code}`);
    await load();
  };

  const openUsage = async (code) => {
    setUsageOpen(code);
    setUsageData(null);
    try {
      const r = await apiClient.get(`/admin/promos/${code}/usage`);
      setUsageData(r.data);
    } catch (e) {
      setUsageData({ error: e?.response?.data?.detail || "Couldn't load usage" });
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="text-sm text-[var(--ink-2)]">
          {items.length} {items.length === 1 ? "code" : "codes"}
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setShowNew((v) => !v)}
            className="btn-primary"
            data-testid="admin-promo-new-toggle"
          >
            <Plus size={14} /> New code
          </button>
        </div>
      </div>

      {showNew && (
        <div className="brut-lg p-6 mb-6" data-testid="admin-promo-new-form">
          <PromoForm
            draft={draft}
            setDraft={setDraft}
            includeCodeField
            testidPrefix="admin-promo-input"
          />
          {error && <div className="mt-3 text-sm text-[var(--accent)]">{error}</div>}
          <div className="mt-5 flex gap-3">
            <button onClick={create} disabled={busy} className="btn-primary" data-testid="admin-promo-create">
              {busy ? <><span className="spinner" /> Creating…</> : <><Plus size={14} /> Create code</>}
            </button>
            <button onClick={() => setShowNew(false)} className="btn-ghost">
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="brut p-10 text-center"><span className="spinner" /></div>
      ) : items.length === 0 ? (
        <div className="brut p-12 text-center text-[var(--ink-2)]">
          No codes yet. Create one to get started.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((p) => {
            const expired =
              p.expires_at && new Date(p.expires_at) < new Date();
            const exhausted =
              p.max_uses != null && (p.uses || 0) >= p.max_uses;
            const live = p.active && !expired && !exhausted;
            const isEditing = editing === p.code;
            return (
              <div
                key={p.code}
                className="brut p-5"
                data-testid={`admin-promo-row-${p.code}`}
              >
                <div className="flex flex-wrap items-start gap-4 justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-mono text-xl tracking-wider font-medium">
                        {p.code}
                      </span>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(p.code);
                          setCopied(p.code);
                          setTimeout(() => setCopied(null), 1300);
                        }}
                        className="text-[var(--ink-3)] hover:text-[var(--ink)] transition-colors"
                        data-testid={`admin-promo-copy-${p.code}`}
                      >
                        {copied === p.code ? <Check size={13} /> : <Copy size={13} />}
                      </button>
                      {live ? (
                        <span className="pill pill-emerald">Active</span>
                      ) : !p.active ? (
                        <span className="pill">Disabled</span>
                      ) : expired ? (
                        <span className="pill" style={{ color: "var(--accent)", borderColor: "rgba(255,92,42,0.3)" }}>Expired</span>
                      ) : (
                        <span className="pill" style={{ color: "var(--accent)", borderColor: "rgba(255,92,42,0.3)" }}>Used up</span>
                      )}
                      {p.new_customers_only && (
                        <span className="pill" style={{ color: "var(--indigo)", borderColor: "rgba(79,70,229,0.3)" }}>
                          New customers only
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-[var(--ink-2)] mt-1.5">
                      {p.discount_type === "percent"
                        ? `${p.discount_value}% off`
                        : p.discount_type === "fixed_price"
                          ? `First month $${Number(p.discount_value).toFixed(2)}`
                          : `$${p.discount_value} off`}
                      {" · "}
                      {p.applies_to === "both" ? "Any plan" : `${p.applies_to[0].toUpperCase()}${p.applies_to.slice(1)} only`}
                      {" · "}
                      {p.uses || 0}/{p.max_uses ?? "∞"} used
                      {p.max_uses_per_user && (
                        <>{" · "}{p.max_uses_per_user}/user</>
                      )}
                      {p.expires_at && (
                        <>
                          {" · "}
                          Expires {new Date(p.expires_at).toLocaleDateString()}
                        </>
                      )}
                    </div>
                    {p.note && (
                      <div className="text-xs text-[var(--ink-3)] mt-1.5">
                        {p.note}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => openUsage(p.code)}
                      className="btn-ghost"
                      style={{ padding: "0.45rem 0.7rem", fontSize: "0.8rem" }}
                      data-testid={`admin-promo-usage-${p.code}`}
                      title="View redemptions"
                    >
                      <BarChart3 size={13} />
                    </button>
                    <button
                      onClick={() => (isEditing ? setEditing(null) : openEdit(p))}
                      className="btn-ghost"
                      style={{ padding: "0.45rem 0.7rem", fontSize: "0.8rem" }}
                      data-testid={`admin-promo-edit-${p.code}`}
                      title={isEditing ? "Cancel edit" : "Edit code"}
                    >
                      {isEditing ? <X size={13} /> : <Pencil size={13} />}
                    </button>
                    <button
                      onClick={() => toggle(p.code)}
                      className="btn-ghost"
                      style={{ padding: "0.45rem 0.9rem", fontSize: "0.8rem" }}
                      data-testid={`admin-promo-toggle-${p.code}`}
                      title={p.active ? "Disable code" : "Enable code"}
                    >
                      <Power size={13} /> {p.active ? "Disable" : "Enable"}
                    </button>
                    <button
                      onClick={() => remove(p.code)}
                      className="btn-ghost"
                      style={{
                        padding: "0.45rem 0.7rem",
                        fontSize: "0.8rem",
                        color: "var(--accent)",
                        borderColor: "rgba(255,92,42,0.3)",
                      }}
                      data-testid={`admin-promo-delete-${p.code}`}
                      title="Delete code"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>

                {isEditing && editDraft && (
                  <div
                    className="mt-5 pt-5 border-t border-[var(--line)]"
                    data-testid={`admin-promo-edit-form-${p.code}`}
                  >
                    <PromoForm
                      draft={editDraft}
                      setDraft={setEditDraft}
                      testidPrefix={`admin-promo-edit-input-${p.code}`}
                    />
                    {editError && (
                      <div className="mt-3 text-sm text-[var(--accent)]">{editError}</div>
                    )}
                    <div className="mt-4 flex gap-3">
                      <button
                        onClick={saveEdit}
                        disabled={busy}
                        className="btn-primary"
                        style={{ padding: "0.5rem 1rem", fontSize: "0.85rem" }}
                        data-testid={`admin-promo-save-${p.code}`}
                      >
                        {busy ? <><span className="spinner" /> Saving…</> : <><Check size={13} /> Save changes</>}
                      </button>
                      <button
                        onClick={() => { setEditing(null); setEditDraft(null); }}
                        className="btn-ghost"
                        style={{ padding: "0.5rem 1rem", fontSize: "0.85rem" }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {usageOpen === p.code && (
                  <div className="mt-5 pt-5 border-t border-[var(--line)]" data-testid={`admin-promo-usage-panel-${p.code}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-sm font-medium text-[var(--ink)]">Redemptions</div>
                      <button
                        onClick={() => { setUsageOpen(null); setUsageData(null); }}
                        className="text-[var(--ink-3)] hover:text-[var(--ink)]"
                        aria-label="Close"
                      >
                        <X size={14} />
                      </button>
                    </div>
                    {!usageData ? (
                      <div className="text-center py-4"><span className="spinner" /></div>
                    ) : usageData.error ? (
                      <div className="text-sm text-[var(--accent)]">{usageData.error}</div>
                    ) : usageData.uses === 0 ? (
                      <div className="text-sm text-[var(--ink-3)]">No redemptions yet.</div>
                    ) : (
                      <div className="text-sm">
                        {usageData.redemptions.map((r) => (
                          <div
                            key={r.session_id}
                            className="flex items-center justify-between py-2 border-b border-[var(--line)] last:border-0 gap-3 flex-wrap"
                          >
                            <div className="min-w-0">
                              <div className="font-medium text-[var(--ink)] truncate">{r.email || "—"}</div>
                              <div className="text-xs text-[var(--ink-3)]">
                                {r.plan} · ${Number(r.amount).toFixed(2)} (was ${Number(r.original_amount || 0).toFixed(2)})
                              </div>
                            </div>
                            <div className="text-xs text-[var(--ink-3)] whitespace-nowrap">
                              {r.created_at ? new Date(r.created_at).toLocaleString() : ""}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Shared form used for both Create and Edit
function PromoForm({ draft, setDraft, includeCodeField = false, testidPrefix }) {
  return (
    <>
      <div className="grid md:grid-cols-2 gap-4">
        {includeCodeField && (
          <Field label="Code" required>
            <input
              type="text"
              className="brut-input uppercase tracking-wider"
              value={draft.code}
              onChange={(e) => setDraft({ ...draft, code: e.target.value.toUpperCase() })}
              placeholder="LAUNCH20"
              data-testid={`${testidPrefix}-code`}
            />
          </Field>
        )}
        <Field label="Discount type">
          <select
            className="brut-input"
            value={draft.discount_type}
            onChange={(e) => setDraft({ ...draft, discount_type: e.target.value })}
            data-testid={`${testidPrefix}-type`}
          >
            <option value="percent">Percent off</option>
            <option value="amount">Dollars off</option>
            <option value="fixed_price">Fixed first-month price</option>
          </select>
        </Field>
        <Field
          label={
            draft.discount_type === "percent"
              ? "Percent off (1-100)"
              : draft.discount_type === "fixed_price"
                ? "First-month price ($)"
                : "Dollars off"
          }
        >
          <input
            type="number"
            className="brut-input"
            min={draft.discount_type === "fixed_price" ? 0 : 0.01}
            max={draft.discount_type === "percent" ? 100 : undefined}
            step={0.01}
            value={draft.discount_value}
            onChange={(e) => setDraft({ ...draft, discount_value: e.target.value })}
            data-testid={`${testidPrefix}-value`}
          />
        </Field>
        <Field label="Applies to">
          <select
            className="brut-input"
            value={draft.applies_to}
            onChange={(e) => setDraft({ ...draft, applies_to: e.target.value })}
            data-testid={`${testidPrefix}-applies`}
          >
            <option value="both">All AI Mastery plans</option>
            <option value="mastery_monthly">Mastery Monthly only</option>
            <option value="mastery_annual">Mastery Annual only</option>
          </select>
        </Field>
        <Field label="Max uses total (blank = unlimited)">
          <input
            type="number"
            className="brut-input"
            min={1}
            value={draft.max_uses}
            onChange={(e) => setDraft({ ...draft, max_uses: e.target.value })}
            placeholder="Unlimited"
            data-testid={`${testidPrefix}-max-uses`}
          />
        </Field>
        <Field label="Max uses per user (default 1)">
          <input
            type="number"
            className="brut-input"
            min={1}
            value={draft.max_uses_per_user ?? ""}
            onChange={(e) => setDraft({ ...draft, max_uses_per_user: e.target.value })}
            placeholder="1"
            data-testid={`${testidPrefix}-max-per-user`}
          />
        </Field>
        <Field label="Expires (optional)">
          <input
            type="date"
            className="brut-input"
            value={draft.expires_at?.slice(0, 10) || ""}
            onChange={(e) => setDraft({ ...draft, expires_at: e.target.value ? `${e.target.value}T23:59:59` : "" })}
            data-testid={`${testidPrefix}-expires`}
          />
        </Field>
        <Field label="New customers only">
          <label className="inline-flex items-center gap-2 mt-2.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={!!draft.new_customers_only}
              onChange={(e) => setDraft({ ...draft, new_customers_only: e.target.checked })}
              data-testid={`${testidPrefix}-new-only`}
            />
            <span className="text-sm text-[var(--ink-2)]">Block users who already have a paid plan</span>
          </label>
        </Field>
      </div>
      <Field label="Internal note (optional)" className="mt-4">
        <input
          type="text"
          className="brut-input"
          value={draft.note}
          onChange={(e) => setDraft({ ...draft, note: e.target.value })}
          placeholder="LinkedIn launch campaign · Q1"
          data-testid={`${testidPrefix}-note`}
        />
      </Field>
    </>
  );
}

function Field({ label, required, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <div className="text-sm font-medium text-[var(--ink)] mb-1.5">
        {label}
        {required && <span className="text-[var(--accent)] ml-0.5">*</span>}
      </div>
      {children}
    </label>
  );
}

// ---------------------------------------------------------------------------
// Analytics — live counts pulled from /api/admin/metrics for a [start, end]
// window. Default window is the user's current local day (00:00 → now). All
// boundaries are computed in the browser's timezone and serialized to UTC
// before they hit the API, so "today" lines up with the operator's clock.
// ---------------------------------------------------------------------------
const ANALYTICS_PRESETS = [
  { id: "today",   label: "Today" },
  { id: "yesterday", label: "Yesterday" },
  { id: "last7",   label: "Last 7 days" },
  { id: "last30",  label: "Last 30 days" },
  { id: "mtd",     label: "Month to date" },
  { id: "custom",  label: "Custom" },
];

function _ymd(d) {
  // Local YYYY-MM-DD (not UTC) so the date inputs match what the operator sees.
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function _rangeForPreset(preset) {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  switch (preset) {
    case "yesterday": {
      const y = new Date(startOfToday); y.setDate(y.getDate() - 1);
      const endY = new Date(startOfToday); endY.setMilliseconds(-1);
      return { from: _ymd(y), to: _ymd(endY) };
    }
    case "last7": {
      const s = new Date(startOfToday); s.setDate(s.getDate() - 6);
      return { from: _ymd(s), to: _ymd(now) };
    }
    case "last30": {
      const s = new Date(startOfToday); s.setDate(s.getDate() - 29);
      return { from: _ymd(s), to: _ymd(now) };
    }
    case "mtd": {
      const s = new Date(now.getFullYear(), now.getMonth(), 1);
      return { from: _ymd(s), to: _ymd(now) };
    }
    case "today":
    default:
      return { from: _ymd(now), to: _ymd(now) };
  }
}

function _boundsToIsoUtc(fromYmd, toYmd) {
  // Convert local-day inputs to UTC ISO bounds [start, end).
  // start = local midnight of `from`
  // end   = local midnight of (`to` + 1 day)
  const [fy, fm, fd] = fromYmd.split("-").map(Number);
  const [ty, tm, td] = toYmd.split("-").map(Number);
  const start = new Date(fy, fm - 1, fd, 0, 0, 0, 0);
  const end = new Date(ty, tm - 1, td, 0, 0, 0, 0);
  end.setDate(end.getDate() + 1);
  return { startIso: start.toISOString(), endIso: end.toISOString() };
}

function AnalyticsPanel() {
  const [preset, setPreset] = useState("today");
  const initial = _rangeForPreset("today");
  const [fromDate, setFromDate] = useState(initial.from);
  const [toDate, setToDate] = useState(initial.to);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const applyPreset = (id) => {
    setPreset(id);
    if (id !== "custom") {
      const r = _rangeForPreset(id);
      setFromDate(r.from);
      setToDate(r.to);
    }
  };

  const load = async (from, to) => {
    setLoading(true);
    setError(null);
    try {
      const { startIso, endIso } = _boundsToIsoUtc(from, to);
      const r = await apiClient.get("/admin/metrics", {
        params: { start: startIso, end: endIso },
      });
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Could not load analytics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (fromDate && toDate && fromDate <= toDate) {
      load(fromDate, toDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate]);

  const onFromChange = (v) => {
    setPreset("custom");
    setFromDate(v);
  };
  const onToChange = (v) => {
    setPreset("custom");
    setToDate(v);
  };

  const stats = data?.totals;
  const cards = [
    { id: "visits",            label: "Visits",            icon: Users,       value: stats?.visits, hint: "Unique sessions in range" },
    { id: "lessons_started",   label: "Lessons started",   icon: PlayCircle,  value: stats?.lessons_started, hint: "First-time lesson attempts" },
    { id: "lessons_completed", label: "Lessons completed", icon: Award,       value: stats?.lessons_completed, hint: "First-time passes" },
    { id: "paid_signups",      label: "Paid signups",      icon: CreditCard,  value: stats?.paid_signups, hint: "Credited subscriptions" },
  ];

  return (
    <div data-testid="admin-analytics-panel">
      {/* Controls */}
      <div className="brut p-5 mb-6">
        <div className="flex items-start gap-3 mb-4">
          <TrendingUp size={18} className="text-[var(--primary,#2563EB)] mt-1 flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="kicker">Live metrics</div>
            <h3 className="font-display text-xl leading-tight mt-0.5">
              How did PromptDojo move today?
            </h3>
            <p className="text-xs text-[var(--ink-3)] mt-1.5 leading-relaxed">
              Visits are unique browser sessions. Lessons started counts the first attempt on a given lesson per user; completed counts the first pass. Paid signups are confirmed subscription transactions (excludes credit-pack purchases).
            </p>
          </div>
        </div>

        <div className="inline-flex flex-wrap gap-1 mb-4 p-0.5 rounded-full border border-[var(--line-strong)] bg-[var(--surface)]">
          {ANALYTICS_PRESETS.map((p) => (
            <button
              key={p.id}
              onClick={() => applyPreset(p.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                preset === p.id ? "bg-[var(--ink)] text-white" : "text-[var(--ink-2)] hover:text-[var(--ink)]"
              }`}
              data-testid={`admin-analytics-preset-${p.id}`}
            >
              {p.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-md">
          <Field label="From">
            <input
              type="date"
              className="brut-input"
              value={fromDate}
              max={toDate}
              onChange={(e) => onFromChange(e.target.value)}
              data-testid="admin-analytics-from"
            />
          </Field>
          <Field label="To">
            <input
              type="date"
              className="brut-input"
              value={toDate}
              min={fromDate}
              onChange={(e) => onToChange(e.target.value)}
              data-testid="admin-analytics-to"
            />
          </Field>
        </div>
      </div>

      {/* Stat cards */}
      {error ? (
        <div className="brut p-6 text-sm text-[var(--accent)]" data-testid="admin-analytics-error">
          {error}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {cards.map(({ id, label, icon: Icon, value, hint }) => (
              <div
                key={id}
                className="brut p-5"
                data-testid={`admin-analytics-card-${id}`}
              >
                <div className="flex items-center gap-2 mb-3 text-[var(--ink-3)]">
                  <Icon size={15} />
                  <span className="text-xs font-semibold uppercase tracking-wider">{label}</span>
                </div>
                <div
                  className="font-display text-4xl leading-none tracking-tight"
                  data-testid={`admin-analytics-value-${id}`}
                >
                  {loading ? <span className="spinner" /> : (value ?? 0).toLocaleString()}
                </div>
                <div className="text-[11px] text-[var(--ink-3)] mt-2 leading-snug">{hint}</div>
              </div>
            ))}
          </div>

          <PromoActivity data={data?.promos} loading={loading} />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Promo activity — second section of the Analytics tab. Two summary stats
// (redemptions + total discount given) plus a leaderboard of the codes that
// drove the most redemptions in the selected window. All numbers come from
// the same `/api/admin/metrics` call as the top stat cards.
// ---------------------------------------------------------------------------
function PromoActivity({ data, loading }) {
  const redemptions = data?.redemptions ?? 0;
  const totalDiscount = data?.total_discount ?? 0;
  const top = data?.top_codes ?? [];

  return (
    <div className="mt-7" data-testid="admin-analytics-promos">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <span className="kicker">Promo activity</span>
          <h3 className="font-display text-xl mt-1 leading-tight">
            Which codes are pulling weight?
          </h3>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div className="brut p-5" data-testid="admin-analytics-card-promo_redemptions">
          <div className="flex items-center gap-2 mb-3 text-[var(--ink-3)]">
            <Tag size={15} />
            <span className="text-xs font-semibold uppercase tracking-wider">Promo redemptions</span>
          </div>
          <div
            className="font-display text-4xl leading-none tracking-tight"
            data-testid="admin-analytics-value-promo_redemptions"
          >
            {loading ? <span className="spinner" /> : redemptions.toLocaleString()}
          </div>
          <div className="text-[11px] text-[var(--ink-3)] mt-2 leading-snug">
            Credited transactions that used a promo
          </div>
        </div>

        <div className="brut p-5" data-testid="admin-analytics-card-promo_discount">
          <div className="flex items-center gap-2 mb-3 text-[var(--ink-3)]">
            <TrendingUp size={15} />
            <span className="text-xs font-semibold uppercase tracking-wider">Total discount given</span>
          </div>
          <div
            className="font-display text-4xl leading-none tracking-tight"
            data-testid="admin-analytics-value-promo_discount"
          >
            {loading ? (
              <span className="spinner" />
            ) : (
              `$${totalDiscount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            )}
          </div>
          <div className="text-[11px] text-[var(--ink-3)] mt-2 leading-snug">
            Sum of (list price − charged amount)
          </div>
        </div>
      </div>

      <div className="brut p-5">
        <div className="flex items-center gap-2 mb-3 text-[var(--ink-3)]">
          <BarChart3 size={14} />
          <span className="text-xs font-semibold uppercase tracking-wider">
            Top codes by redemptions
          </span>
        </div>
        {loading ? (
          <div className="py-6 text-center"><span className="spinner" /></div>
        ) : top.length === 0 ? (
          <div className="text-sm text-[var(--ink-3)] py-2" data-testid="admin-analytics-top-codes-empty">
            No promo redemptions in this range.
          </div>
        ) : (
          <div className="divide-y divide-[var(--line)]" data-testid="admin-analytics-top-codes">
            {top.map((row, idx) => (
              <div
                key={row.code}
                className="flex items-center justify-between py-2.5 gap-3"
                data-testid={`admin-analytics-top-code-${row.code}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-xs text-[var(--ink-3)] font-mono w-5 flex-shrink-0">
                    {idx + 1}
                  </span>
                  <span className="font-mono text-sm uppercase tracking-wider truncate">
                    {row.code}
                  </span>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-sm font-semibold text-[var(--ink)]">
                    {row.redemptions.toLocaleString()} redemption{row.redemptions === 1 ? "" : "s"}
                  </div>
                  <div className="text-[11px] text-[var(--ink-3)]">
                    ${Number(row.total_discount || 0).toFixed(2)} discount
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
